import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { PROFILES } from "../lib/profiles";
import { waLink } from "../lib/site";
import { scrollToId } from "./SmoothScroll";

const ANCHORS = [
  { id: "processo", label: "Como atuamos" },
  { id: "faq", label: "Dúvidas" },
  { id: "contato", label: "Contato" },
];

const useAnchor = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (id) => (e) => {
    e.preventDefault();
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };
};

const ProfilesMenu = () => (
  <div className="group relative">
    <button data-testid="nav-profiles-trigger" className="nav-link flex items-center gap-1">
      Seu caso <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
    </button>
    <div className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 pt-4 opacity-0 translate-y-2 transition-[opacity,transform] duration-300 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0">
      <div className="w-[340px] border border-[#D4AF37]/25 bg-[#0B132B]/95 backdrop-blur-xl p-2">
        {PROFILES.map((p) => (
          <Link key={p.slug} to={`/perfil/${p.slug}`} data-testid={`nav-profile-${p.slug}`} className="flex items-start gap-4 p-3 hover:bg-[#17254D] transition-colors duration-300">
            <span className="font-mono text-[11px] text-[#D4AF37] pt-1">{p.index}</span>
            <span>
              <span className="block text-sm text-white">{p.title}</span>
              <span className="block text-xs text-[#AEB9D0] mt-1">{p.kicker}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const MobileMenu = ({ onClose, go }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="fixed inset-0 z-[60] bg-[#0B132B] px-6 pt-24 pb-10 flex flex-col lg:hidden"
    data-testid="mobile-menu"
  >
    <button onClick={onClose} data-testid="mobile-menu-close" className="absolute top-6 right-6 text-white"><X className="w-7 h-7" /></button>
    <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Seu caso</p>
    {PROFILES.map((p) => (
      <Link key={p.slug} to={`/perfil/${p.slug}`} onClick={onClose} data-testid={`mobile-profile-${p.slug}`} className="flex items-baseline gap-4 py-3 border-b border-white/10">
        <span className="font-mono text-xs text-[#D4AF37]">{p.index}</span>
        <span className="font-display text-2xl text-white">{p.label}</span>
      </Link>
    ))}
    <div className="mt-8 flex flex-col gap-3">
      {ANCHORS.map((a) => (
        <a key={a.id} href={`/#${a.id}`} onClick={(e) => { onClose(); go(a.id)(e); }} data-testid={`mobile-anchor-${a.id}`} className="text-[#C0C9DC] text-lg">{a.label}</a>
      ))}
    </div>
    <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn" className="btn-gold mt-auto justify-center">Falar no WhatsApp</a>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const go = useAnchor();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${scrolled ? "bg-[#0B132B]/85 backdrop-blur-xl border-[#D4AF37]/20 py-3" : "bg-transparent border-transparent py-5"}`}
      >
        <div className="mx-auto max-w-[1360px] px-6 lg:px-10 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link"><Logo /></Link>
          <nav className="hidden lg:flex items-center gap-9">
            <ProfilesMenu />
            {ANCHORS.map((a) => (
              <a key={a.id} href={`/#${a.id}`} onClick={go(a.id)} data-testid={`nav-${a.id}`} className="nav-link">{a.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn" className="hidden md:inline-flex btn-ghost-gold">
              Análise do caso <ArrowUpRight className="w-4 h-4" />
            </a>
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="lg:hidden text-white p-1" aria-label="Abrir menu"><Menu className="w-7 h-7" /></button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} go={go} />}</AnimatePresence>
    </>
  );
};
